import { useEffect, useState } from 'react'
import { fetchAdminOverview } from '@/api/admin'
import { focusRing } from '@/components/ui/buttonStyles'
import type { AdminOverview, AdminTab } from '@/types/admin'

interface OverviewSectionProps {
  active: boolean
  onNavigate: (key: AdminTab) => void
}

const TILES: { key: keyof AdminOverview; label: string; hint: string; goTo: AdminTab }[] = [
  { key: 'users_count', label: 'Users', hint: 'accounts', goTo: 'users' },
  {
    key: 'usable_invitation_codes_count',
    label: 'Open invitations',
    hint: 'codes not yet used',
    goTo: 'invitation_codes',
  },
  { key: 'languages_count', label: 'Languages', hint: 'available to enrol in', goTo: 'languages' },
  { key: 'readings_count', label: 'Readings', hint: 'texts across all learners', goTo: 'readings' },
  { key: 'words_count', label: 'Words', hint: 'lemmas in the lexicon', goTo: 'words' },
  {
    key: 'active_enrichment_runs_count',
    label: 'Runs in progress',
    hint: 'enrichment jobs queued or working',
    goTo: 'enrichment_runs',
  },
]

/**
 * Where the panel opens. Every count is a door: the tile links to the section
 * that manages it, so the overview doubles as the panel's table of contents.
 */
export function OverviewSection({ active, onNavigate }: OverviewSectionProps) {
  const [counts, setCounts] = useState<AdminOverview | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!active || counts) return
    fetchAdminOverview()
      .then((res) => setCounts(res.data.data))
      .catch(() => setError('Could not load the overview. Reload to try again.'))
  }, [active, counts])

  if (!active) return null
  if (error) return <p role="alert" className="font-serif text-sm text-body">{error}</p>

  return (
    <ul
      aria-busy={counts === null}
      className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3"
    >
      {TILES.map((tile) => (
        <li key={tile.key}>
          <a
            href={`/admin?section=${tile.goTo}`}
            onClick={(e) => {
              e.preventDefault()
              onNavigate(tile.goTo)
            }}
            className={`flex w-full flex-col items-start gap-1 rounded-lg border border-border-divider bg-surface-bg/40 px-5 py-4 text-left transition-colors duration-200 hover:bg-surface-bg ${focusRing}`}
          >
            <span className="font-mono text-xs uppercase tracking-widest text-muted">{tile.label}</span>
            {counts ? (
              <span className="font-display text-3xl text-heading tabular-nums">
                {counts[tile.key].toLocaleString()}
              </span>
            ) : (
              <span className="mt-1 h-8 w-16 animate-pulse rounded bg-surface-bg" />
            )}
            <span className="font-serif text-sm text-muted">{tile.hint}</span>
          </a>
        </li>
      ))}
    </ul>
  )
}
